import { Capacitor } from '@capacitor/core'
import { Network } from '@capacitor/network'
import React, { createContext, useEffect, useState } from 'react'
import { useThemeHandler } from '../hooks/useThemeHandler'
import { trpc } from '../modules/trpc'

interface AppContextType {
	appActive: boolean
	networkConnected: boolean
	checkNetwork: () => void
	checkingNetwork: boolean
	sufficientVersion: boolean
	serverConnectionHealthy: boolean
	networkFetching: boolean
	retryServerConnection: () => void
	serverErrorMessage: string
	isNativePlatform: boolean
	isMobileView: boolean
	menuOpen: boolean
	toggleMenu: () => void
	isDarkMode: boolean
	setThemeMode: (mode: 'dark' | 'light' | 'system') => void
}

export const AppContext = createContext({} as AppContextType)

const AppContextProvider = (props: any) => {
	const [appActive, setAppActive] = useState(true)
	const [networkConnected, setNetworkConnected] = useState(true)
	const [checkingNetwork, setCheckingNetwork] = useState(false)
	const [sufficientVersion, setSufficientVersion] = useState(true)
	const [serverConnectionHealthy, setServerConnectionHealthy] = useState(true)
	const [serverErrorMessage, setServerErrorMessage] = useState<string>('')
	const [isDarkMode, setIsDarkMode] = useState(false)
	const [menuOpen, setMenuOpen] = useState(false)
	const isNativePlatform = Capacitor.isNativePlatform()

	const [isMobileView, setIsMobileView] = useState(isNativePlatform || window.innerWidth < 640)

	const { setThemeMode } = useThemeHandler({
		onThemeChange: (isDark: boolean) => setIsDarkMode(isDark),
	})

	const platform = Capacitor.getPlatform()

	const { refetch: retryServerConnection, isFetching: networkFetching } =
		trpc.statistics.getMinAppVersion.useQuery(
			{},
			{
				onSuccess: data => {
					setServerConnectionHealthy(true)

					if (data.minVersion === 'override') return setSufficientVersion(true)

					const minVersion = data.minVersion.split('.').map(parseFloat)
					const appVersion = process.env.REACT_APP_VERSION!.split('.').map(parseFloat)

					if (minVersion.length < 3 || appVersion.length < 3) return

					if (appVersion[0] < minVersion[0]) return setSufficientVersion(false)
					else if (appVersion[0] > minVersion[0]) return setSufficientVersion(true)
					else if (appVersion[1] < minVersion[1]) return setSufficientVersion(false)
					else if (appVersion[1] > minVersion[1]) return setSufficientVersion(true)
					else if (appVersion[2] < minVersion[2]) return setSufficientVersion(false)
					else if (appVersion[2] > minVersion[2]) return setSufficientVersion(true)

					return setSufficientVersion(true)
				},
				onError: () => {
					// console.log('Vae server not responding!')
					setServerConnectionHealthy(false)
				},
				staleTime: 1000 * 60, // 1 minute
			}
		)

	const { refetch: refetchServerErrorMessage } = trpc.statistics.getServerErrorMessage.useQuery(
		{},
		{
			onSuccess: data => {
				switch (platform) {
					case 'ios':
						setServerErrorMessage(data.iosErrorMessage)
						break
					case 'android':
						setServerErrorMessage(data.androidErrorMessage)
						break
					case 'web':
						setServerErrorMessage(data.webErrorMessage)
						break
					default:
						setServerErrorMessage('')
						break
				}
			},
		}
	)

	const checkNetwork = async () => {
		setCheckingNetwork(true)
		const status = await Network.getStatus()
		setNetworkConnected(status.connected)
		setCheckingNetwork(false)
		refetchServerErrorMessage()
	}

	const toggleMenu = () => {
		setMenuOpen(state => !state)
	}

	useEffect(() => {
		document.addEventListener('pause', () => setAppActive(false), false)
		document.addEventListener('resume', () => setAppActive(true), false)

		Network.addListener('networkStatusChange', status => {
			setNetworkConnected(status.connected)
		})

		const handleResize = () => {
			setIsMobileView(isNativePlatform || window.innerWidth < 640)
		}

		window.addEventListener('resize', handleResize)

		return () => {
			window.removeEventListener('resize', handleResize)
		}

		// checkNetwork()
	}, [])

	return (
		<AppContext.Provider
			value={{
				appActive,
				networkConnected,
				checkNetwork,
				checkingNetwork,
				sufficientVersion,
				serverConnectionHealthy,
				retryServerConnection,
				networkFetching,
				serverErrorMessage,
				isNativePlatform,
				isMobileView,
				menuOpen,
				toggleMenu,
				isDarkMode,
				setThemeMode,
			}}
		>
			{props.children}
		</AppContext.Provider>
	)
}

export default AppContextProvider
